import { useRef } from "preact/hooks";

import { FlexibleTaskRow } from "../components/FlexibleTaskRow";
import { ParentCard } from "../components/ParentCard";
import { ReminderOptInCard } from "../components/ReminderOptInCard";
import type { ReminderGuidance as ReminderGuidanceSteps } from "../lib/reminderGuidance";
import { TaskList } from "../components/TaskList";
import { TodayHeatPill } from "../components/TodayHeatPill";
import { TodayProgress } from "../components/TodayProgress";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import type { GroupKey } from "../hooks/useDue";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useWeather } from "../hooks/useWeather";
import { weatherIconGlyph, weatherStripTempLabel } from "../lib/weatherCopy";
import type { DueResponse, DueTask, FlexibleProgress } from "../types";

export interface TodayViewProps {
  data: DueResponse;
  onToggle: (task: DueTask, group: GroupKey) => void;
  /** Sprint-003 A6: only a person who `can_hand_out_work` sees the CTA
   * and can open a delegated job's detail (design-spec-today-card.md §2's
   * "Hand out work CTA (manager/admin only)"). */
  canHandOutWork: boolean;
  onHandOutWork: () => void;
  onOpenParent: (parentAssignmentId: string) => void;
  /** Sprint-006 D5 (F-D4-1) — logs a completion against a flexible/"aim
   * for" cadence, same handler `MyListView` already wires to
   * `useDue()`'s `logFlexibleTask`. */
  onLogFlexible: (item: FlexibleProgress) => void;
  /** Sprint-005 C6 — `hooks/useReminderOptIn.ts`'s heuristic. Optional/
   * defaulted so every existing render/test of this view (written before
   * reminders existed) keeps working unchanged. */
  showReminderPrompt?: boolean;
  onAcceptReminders?: () => void;
  onDeclineReminders?: () => void;
  /** Steps this device needs first (iPhone/iPad not yet on the Home
   * Screen, or on old iOS) — the card shows them instead of "Yes please". */
  reminderGuidance?: ReminderGuidanceSteps | null;
}

/**
 * Today view (design-spec-today-card.md §2/§3.7): the "Hand out work" CTA
 * (manager/admin only), then parent/delegated job roll-ups, then Past
 * due, Due today, Whenever you get a chance, Done today — in that order,
 * matching the validated mock exactly. Sprint-003 A5: this is always the
 * signed-in person's OWN personal list (`GET /api/v1/due?scope=mine`) —
 * per-row assignee badges collapse for the person's own rows (see
 * `TaskRow.tsx`'s `assigneeBadge`), matching design-qa-sprint-002.md's
 * recommendation #2 exactly.
 *
 * Sprint-005 C6 addition: the reminders opt-in card, when shown, renders
 * FIRST — above the hand-out-work CTA — every role may see it (unlike the
 * CTA), and it is the least "task work," most dismissible thing on the
 * screen, so it earns the top slot without displacing anything the
 * design spec already ordered.
 */
export function TodayView({
  data,
  onToggle,
  canHandOutWork,
  onHandOutWork,
  onOpenParent,
  onLogFlexible,
  showReminderPrompt = false,
  onAcceptReminders,
  onDeclineReminders,
  reminderGuidance = null,
}: TodayViewProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // Sprint-008 A2 (R-A0-2) — route-change focus management. `<h1
  // tabIndex={-1}>` + `useFocusHeadingOnMount` is the SAME governed
  // convention `LoginView`/`CredentialSetupView`/every other routed view
  // in this sprint reuses (`ux-auth-v2-flows.md` §5) — not a second one
  // invented for a visually-hidden heading. A screen reader announces
  // "Today" on this focus move the same as any other view; a sighted
  // keyboard user tabbing right after a sidebar/Today-row click sees the
  // global focus ring around this (invisible) element, same trade-off
  // this view already accepted for its `.sr-only` heading pre-A2.
  useFocusHeadingOnMount(headingRef);

  // Sprint-009 B5 (design-phase-b.md §3.4) — a one-line, non-card weather
  // strip in Today's own header, phone only (the desktop Dashboard card
  // already shows the full forecast — §3.4's own "deliberate asymmetry"
  // note). Rendered ONLY when `available: true` and the fetch has
  // resolved — nothing at all otherwise (no loading/unavailable/error
  // placeholder here; Today's header had no weather-shaped hole before
  // this sprint, and inventing one only to fill it with an empty state
  // would be new dead chrome, per §3.4's own rationale).
  const isDesktop = useIsDesktop();
  const weather = useWeather();
  const current = weather.weather?.available ? weather.weather.current : null;
  // Sprint-015 C5 (weather-hub-ia-layout-2026-07-10.md §6, CD sign-off
  // Deviation 3) — the Today Card's own new conditional pill. Independent
  // of `available`/`current` (like the Dashboard's own NWS banner, SA
  // Amendment 1 condition 1) — an active alert or a computed advisory can
  // exist even when the forecast half of the same cached response is
  // temporarily unavailable, and the precedence rule (NWS > heat-stress >
  // absent) doesn't reference forecast availability at all.
  const todayAdvisory = weather.weather?.advisory ?? null;
  const todayAlerts = weather.weather?.alerts ?? [];

  return (
    <section class="view" aria-label="Today">
      {/* Sprint-006 D3 — a real heading matching the section's own
       * aria-label. On phone, every other tab (My list/Team/Admin/More)
       * renders its own visible `<h1 class="view-heading">`; Today
       * deliberately shows "Good morning, <name>" in the app-bar instead
       * (shared chrome across every tab, so it can't itself serve as this
       * view's own on-screen heading) — this keeps the same "one real h1
       * per view" structure for screen-reader navigation without
       * duplicating the greeting visually.
       *
       * Sprint-011 A4 (P4) — that app-bar is phone-gated (`app.tsx`'s
       * `isDesktop ? null : <header class="app-bar">`), so desktop had NO
       * visible title at all — the only routed screen with no desktop
       * pass. Desktop gets the same visible `.page-header`/`.view-heading`
       * every other tab already uses; phone keeps the `.sr-only` heading
       * unchanged (the greeting still owns that surface there). */}
      {isDesktop ? (
        <header class="page-header">
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            Today
          </h1>
        </header>
      ) : (
        <h1 ref={headingRef} tabIndex={-1} class="sr-only">
          Today
        </h1>
      )}

      {!isDesktop && current ? (
        <p class="weather-strip">
          <span class="weather-strip-icon" aria-hidden="true">
            {weatherIconGlyph(current.condition_code)}
          </span>
          {weatherStripTempLabel(current.temp_f)} · {current.condition_label}
        </p>
      ) : null}

      {/* Sprint-015 C5 — directly below the untouched `.weather-strip`
       * above, purely additive. `.weather-strip` keeps rendering
       * unconditionally on mobile regardless of advisory/alert state;
       * this new pill is its own element, not a modification of it. */}
      {!isDesktop ? <TodayHeatPill advisory={todayAdvisory} alerts={todayAlerts} /> : null}

      <TodayProgress data={data} />

      {showReminderPrompt && onAcceptReminders && onDeclineReminders ? (
        <ReminderOptInCard
          onAccept={onAcceptReminders}
          onDecline={onDeclineReminders}
          guidance={reminderGuidance}
        />
      ) : null}

      {canHandOutWork ? (
        <button type="button" class="handout-btn" onClick={onHandOutWork}>
          <span aria-hidden="true">＋</span> Hand out work
        </button>
      ) : null}

      {data.parent_progress.map((p) => (
        <ParentCard
          key={p.parent_assignment_id}
          progress={p}
          onOpen={canHandOutWork ? () => onOpenParent(p.parent_assignment_id) : undefined}
        />
      ))}

      <TaskList
        label="Overdue"
        labelClass="overdue"
        tasks={data.overdue}
        group="overdue"
        onToggle={onToggle}
      />
      <TaskList
        label="Due today"
        labelClass="due"
        tasks={data.due_today}
        group="due_today"
        onToggle={onToggle}
      />
      <TaskList
        label="Whenever you get a chance"
        labelClass="whenever"
        tasks={data.whenever}
        group="whenever"
        onToggle={onToggle}
      />

      {/* Sprint-006 D5 (F-D4-1) — mirrors the "Whenever you get a chance"
       * pattern exactly (M6): renders only when at least one flexible/
       * "aim for" cadence exists, so it never adds an empty section to
       * the one screen the whole app promises shows "everything due."
       * Without this, a flexible chore (e.g. "Muck chicken run 1-2x/
       * week") was only ever visible in My list — see
       * ux-review-sprint-006.md F-D4-1. */}
      {data.flexible_progress.length > 0 ? (
        <>
          <h2 class="section-label flexible">This week</h2>
          <ul class="task-list">
            {data.flexible_progress.map((item) => (
              <FlexibleTaskRow key={item.cadence_id} item={item} onLog={onLogFlexible} />
            ))}
          </ul>
        </>
      ) : null}

      <TaskList
        label="Done today"
        labelClass="done"
        tasks={data.done_today}
        group="done_today"
        onToggle={onToggle}
        emptyText="Nothing here yet today."
      />
    </section>
  );
}
